import { useMemo, useState } from 'react';
import { Alert, Button, Form as AntForm, Grid, Input, Typography } from 'antd';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { CheckCircle2, Mail } from 'lucide-react';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { authApi } from '@api/auth';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { ROUTES } from '@shared/constants/routes';
import { recordAuditAction } from '@features/audit/recordAction';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function makeSchema(t: Translator) {
  return z.object({
    email: z
      .string()
      .min(1, t('validation:emailRequired'))
      .email(t('validation:email')),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common', 'validation', 'errors']);
  const screens = useBreakpoint();
  const isWide = screens.lg;
  const [serverError, setServerError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);

  const mutation = useMutation({
    mutationFn: (values: Values) => authApi.forgotPassword(values.email),
    onSuccess: (_data, values) => {
      setSubmittedEmail(values.email);
      void recordAuditAction({
        action: 'auth.password.forgot',
        resource: 'auth',
        summary: `${values.email} için şifre sıfırlama e-postası talep edildi`,
      });
    },
    onError: (err) => {
      setServerError(resolveApiErrorMessage(err));
    },
  });

  return (
    <div className="ds-login-shell">
      {isWide ? (
        <aside className="ds-login-brand" aria-hidden="true">
          <div className="ds-login-brand__inner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="ds-login-brand__mark"><img src="/logo.svg" alt="" /></div>
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{t('brand.title')}</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{t('brand.subtitle')}</div>
              </div>
            </div>
          </div>
          <div className="ds-login-brand__inner" style={{ maxWidth: 420 }}>
            <h2 className="ds-login-brand__heading">{t('forgot.panel.heading')}</h2>
            <p className="ds-login-brand__lead">
              {t('forgot.panel.lead')}
            </p>
          </div>
          <div className="ds-login-brand__footer">
            <span>{t('brand.company', { year: new Date().getFullYear() })}</span>
            <span>{t('brand.version', { version: import.meta.env.VITE_APP_VERSION ?? 'dev' })}</span>
          </div>
        </aside>
      ) : null}

      <section className="ds-login-form-section">
        <div className="ds-login-form">
          {!isWide ? (
            <div className="ds-auth-card__brand">
              <div className="ds-auth-card__brand-mark"><img src="/logo.svg" alt="" /></div>
              <div className="ds-auth-card__brand-text">
                <div className="ds-auth-card__brand-name">{t('brand.title')}</div>
                <div className="ds-auth-card__brand-sub">{t('brand.cardSubtitle')}</div>
              </div>
            </div>
          ) : null}

          {submittedEmail ? (
            <>
              <div
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(34,197,94,0.12)',
                  color: 'var(--ds-success)',
                  marginBottom: 12,
                }}
              >
                <CheckCircle2 size={24} />
              </div>
              <Title level={3} className="ds-auth-card__title" style={{ marginBottom: 4 }}>
                {t('forgot.sent.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                <Trans
                  i18nKey="forgot.sent.description"
                  ns="auth"
                  values={{ email: submittedEmail }}
                  components={[<strong key="email" />]}
                />
              </Text>
              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 16 }}
                onClick={() => {
                  setSubmittedEmail(null);
                }}
              >
                {t('forgot.sent.tryAnother')}
              </Button>
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <Link to={ROUTES.LOGIN}>{t('forgot.backToLogin')}</Link>
              </div>
            </>
          ) : (
            <>
              <Title level={3} className="ds-auth-card__title" style={{ marginBottom: 4 }}>
                {t('forgot.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('forgot.subtitle')}
              </Text>

              {serverError ? (
                <Alert
                  type="error"
                  message={serverError}
                  showIcon
                  closable
                  onClose={() => setServerError(null)}
                  style={{ marginBottom: 16 }}
                />
              ) : null}

              <Form
                schema={schema}
                defaultValues={{ email: '' }}
                onSubmit={(values) => mutation.mutate(values)}
              >
                <AntForm layout="vertical" component="div">
                  <FormField name="email" label={t('forgot.fields.emailLabel')} required>
                    <Input
                      type="email"
                      autoComplete="username"
                      placeholder={t('forgot.fields.emailPlaceholder')}
                      size="large"
                      prefix={<Mail size={14} />}
                    />
                  </FormField>
                </AntForm>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={mutation.isPending}
                >
                  {t('forgot.submit')}
                </Button>
              </Form>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Link to={ROUTES.LOGIN}>{t('forgot.backToLogin')}</Link>
              </div>
            </>
          )}

          {!isWide ? (
            <div className="ds-login-form__footer-mobile">
              {t('brand.footerMobile', {
                year: new Date().getFullYear(),
                version: import.meta.env.VITE_APP_VERSION ?? 'dev',
              })}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
